import { OrderItem } from 'types/pwcOrder';

import { makeLabels } from 'utils/i18n';

export enum AvailableRefundDuration {
  Hours = 'hours',
  Days = 'days',
  Months = 'months',
}

export const NAMESPACE = 'page.pwcOrders.view.sections.order.refund';

export const KEYS = {
  FORM_FIELDS: {
    ITEMS: 'items',
    FIAT_AMOUNT: 'fiatAmount',
    EMAIL: 'email',
    REFUND_TTL: 'refundTtl',
    EXTERNAL_REFUND_ID: 'externalRefundId',
    REDIRECT_URL: 'redirectUrl',
    CANCEL_REDIRECT_URL: 'cancelRedirectUrl',
    WEBHOOK_URL: 'webhookUrl',
  },
} as const;

export const LABELS = makeLabels(
  {
    MODAL_TITLE: ['modalTitle', 'Refund Charge'],
    FIELDS: {
      [KEYS.FORM_FIELDS.ITEMS]: ['fields.items', 'Refund Summary'],
      [KEYS.FORM_FIELDS.FIAT_AMOUNT]: ['fields.fiatAmount', 'Refund Amount in {{currencyCode}}'],
      [KEYS.FORM_FIELDS.EMAIL]: ['fields.email', 'Customer Email'],
      [KEYS.FORM_FIELDS.REFUND_TTL]: ['fields.refundTtl', 'Refund Expiration Time'],
      [KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID]: ['fields.externalRefundId', 'External Refund ID'],
      [KEYS.FORM_FIELDS.REDIRECT_URL]: ['fields.redirectUrl', 'Redirect URL'],
      [KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL]: ['fields.cancelRedirectUrl', 'Cancel Redirect URL'],
      [KEYS.FORM_FIELDS.WEBHOOK_URL]: ['fields.webhookUrl', 'Webhook URL'],
    },
    PLACEHOLDERS: {
      [KEYS.FORM_FIELDS.EMAIL]: ['placeholders.email', 'Enter Customer Email'],
      [KEYS.FORM_FIELDS.REFUND_TTL]: ['placeholders.refundTtl', 'Expired in'],
      [KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID]: ['placeholders.externalRefundId', 'Enter Merchant Order ID'],
      [KEYS.FORM_FIELDS.REDIRECT_URL]: ['placeholders.redirectUrl', 'Enter Successful Redirect URL'],
      [KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL]: ['placeholders.cancelRedirectUrl', 'Enter Cancel Redirect URL'],
    },
    ERRORS: {
      MIN_AMOUNT: ['errors.minAmount', 'Refund amount must be at least 1 {{currencyCode}}'],
      MIN_AMOUNT_POSITIVE: ['errors.minAmountPositive', 'Refund amount must be greater than 0'],
      MIN_SETTLEMENT: ['errors.minSettlement', 'Refund amount must be at least 1 USDC equivalent'],
      MAX_AMOUNT: ['errors.maxAmount', 'Refund amount can not be higher than the refundable amount'],
      REQUIRED: ['errors.required', 'This field is required'],
      EMAIL: ['errors.email', 'Invalid email address'],
      URL: ['errors.url', 'Invalid URL format'],
      GENERIC: ['errors.generic', 'An error occurred, please try again later'],
    },
    SECTIONS: {
      ORDER_DETAILS: {
        ID: ['orderDetails.id', 'Order ID'],
        REFUNDABLE_AMOUNT: ['orderDetails.refundableAmount', 'Refundable Amount'],
      },
      ADVANCED_SETTINGS: {
        TITLE: ['advancedSettings.title', 'Advanced Settings'],
      },
    },
    DURATION: {
      [AvailableRefundDuration.Hours]: ['hours', 'Hours'],
      [AvailableRefundDuration.Days]: ['days', 'Days'],
      [AvailableRefundDuration.Months]: ['months', 'Months'],
    },
    CONFIRMATION_MODAL: {
      QUESTION: ['confirmationModal.question', 'Are you sure?'],
      REMARK: [
        'remark',
        'The refund of <b>{{amountWithCurrency}}</b> will be processed.<br>' +
          'An email will be sent to the customer to confirm the refund.',
      ],
    },
    ACTIONS: {
      CANCEL: ['actions.cancel', 'Cancel'],
      REFUND: ['actions.refund', 'Refund'],
      BACK: ['actions.back', 'Back'],
      DONE: ['actions.done', 'Done'],
      ADD_ITEM: ['actions.addItem', 'ADD NEW ITEM'],
      SELECT_ALL: ['actions.selectAll', 'Select All'],
      DESELECT_ALL: ['actions.deselectAll', 'Deselect All'],
      CONFIRM: ['actions.confirm', 'Confirm'],
    },
    MESSAGES: {
      CREATION_SUCCESS: ['messages.creationSuccess', 'Refund successfully issued'],
      EMAIL_ERROR_MESSAGE: [
        'messages.emailErrorMessage',
        'This refund can’t be processed without the customer’s email. Please reach out to support.crypto@shift4.com for assistance.',
      ],
    },
  },
  NAMESPACE,
);

export const PLAIN_LABELS = {
  PLACEHOLDERS: {
    [KEYS.FORM_FIELDS.WEBHOOK_URL]: 'https://',
  },
};

export type RefundFormField = (typeof KEYS.FORM_FIELDS)[keyof typeof KEYS.FORM_FIELDS];
export type RefundFormValues = {
  [KEYS.FORM_FIELDS.FIAT_AMOUNT]: string | undefined;
  [KEYS.FORM_FIELDS.ITEMS]: OrderItem[];
  [KEYS.FORM_FIELDS.EMAIL]: string | undefined;
  [KEYS.FORM_FIELDS.REFUND_TTL]: number | undefined;
  [KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID]: string | undefined;
  [KEYS.FORM_FIELDS.REDIRECT_URL]: string | undefined;
  [KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL]: string | undefined;
  [KEYS.FORM_FIELDS.WEBHOOK_URL]: string | undefined;
};
